/**
 * The note page's processing row — which stage a note's capture is in
 * (#14241).
 *
 * `/notes/[id]` shows ONE row that walks upload -> transcribing ->
 * summarizing -> extracting -> done. No column stores that stage; it is
 * folded here from three facts the page already reads:
 *
 *   - the latest `Recording.status` (`RECORDING_STATUSES`), which the capture
 *     path, the transcription runner and the extraction claim each advance;
 *   - the latest `TranscriptionJob.status`, which is the only authority on
 *     whether transcription is still running (a retried job re-runs over a
 *     recording already labelled `TRANSCRIBED`);
 *   - whether a summary row exists — the summary stage writes no status of
 *     its own (`summary-stage-fold.ts`), so its output IS its status — and
 *     whether a transcript finished after the pass that wrote it started,
 *     which makes it an earlier part's summary (#15145).
 *
 * ## The row walks forward only
 *
 * The sweep EXTRACTS before it SUMMARISES (`transcribe-recordings/sweep.ts`),
 * but the issue orders the row summarizing -> extracting. Keying SUMMARIZING
 * on a recording label would therefore read TRANSCRIBED -> summarizing,
 * EXTRACTING -> extracting, READY -> summarizing: backwards. So after
 * transcription the row asks the two completion facts in the ROW's order —
 * no summary yet is SUMMARIZING, a summary with extraction unfinished is
 * EXTRACTING, both is DONE — and every row the real pipeline writes lands
 * monotonically on it.
 *
 * ## A quiet pipeline settles
 *
 * An environment with no extraction or summary credential leaves a recording
 * at `TRANSCRIBED` for good, and a failed summary pass leaves no row to find.
 * A row that said "Summarizing" forever would be a spinner for work nobody is
 * doing, so a post-transcription state whose recording has not moved for
 * {@link NOTE_PROCESSING_SETTLE_MS} is DONE. A RUNNING transcription never
 * settles: the job row, not the clock, is authoritative there.
 *
 * ## ...unless a pass it should have run is missing (#14362)
 *
 * Settling silently was right for the unconfigured environment and wrong for
 * the model timeout: a transcript with no summary under it past the settle
 * window, with a credential configured, is a pass that FAILED (twice — the
 * sweep retries once in the same fire), and the page should say "The summary
 * didn't finish" and offer `retryNoteProcessing` rather than go quiet. That is
 * `PASS_FAILED`: settled like DONE (nothing is running, the row stops
 * polling), distinct from it. Which passes count as missing is
 * `noteMissingPasses` (`./note-pass-retry.ts`), the same predicate the retry
 * action plans from, so the page never offers a retry the action declines.
 *
 * The caller supplies the extra facts in `passes`; without them the fold
 * keeps its pre-#14362 settle, because it cannot tell a failure from a
 * credential that was never there.
 *
 * Pure, and the component tests assert against literals through it.
 */

import type { MeetingTranscriptionStatus } from '@/lib/meetings/transcription-status'
import {
  IMPORTED_RECORDING_MODE,
  type TranscriptionJobStatus,
} from '@/lib/notetaker/vocabularies'

import { noteMissingPasses } from './note-pass-retry'

/** The four in-flight steps, in the order the row renders them. */
export const NOTE_PROCESSING_STEPS = [
  'UPLOADING',
  'TRANSCRIBING',
  'SUMMARIZING',
  'EXTRACTING',
] as const

export type NoteProcessingStep = (typeof NOTE_PROCESSING_STEPS)[number]

/**
 * Where a note is. `NONE` is a notes-only note — nothing was recorded, so
 * there is nothing to process and the page renders no row at all.
 * `PASS_FAILED` is a transcribed note whose summary or extraction pass never
 * produced output (#14362) — settled, but not DONE. `FAILED` is reserved for
 * transcription, which is a different failure with a different recovery.
 */
export type NoteProcessingStage =
  | NoteProcessingStep
  | 'DONE'
  | 'PASS_FAILED'
  | 'FAILED'
  | 'NONE'

/**
 * How long a post-transcription stage may sit unchanged before the row stops
 * claiming work is happening.
 *
 * Ten minutes: the summary and extraction passes run in the same sweep fire
 * as the transcription that precedes them (seconds to a couple of minutes
 * measured), so a recording untouched for ten is not being worked on.
 */
export const NOTE_PROCESSING_SETTLE_MS = 10 * 60 * 1000

/** The reader's word for each step. */
export const NOTE_PROCESSING_STEP_LABELS: Record<NoteProcessingStep, string> = {
  UPLOADING: 'Uploading',
  TRANSCRIBING: 'Transcribing',
  SUMMARIZING: 'Summarizing',
  EXTRACTING: 'Finding items',
}

export type NoteProcessingInput = {
  /** The latest recording's status, or null when the note has none. */
  recordingStatus: string | null
  /**
   * The latest recording's `mode`. Absent reads as a capture. An `IMPORTED`
   * recording (#14412) has no transcription job and no summary pass, and its
   * extraction is gated by the importer's synopsis-access check, which the
   * retry action cannot make — so the row never reads PASS_FAILED for one.
   */
  recordingMode?: string | null
  /** The latest transcription job's status, or null when none exists. */
  transcriptionStatus: TranscriptionJobStatus | null
  /** Whether the note has a stored summary. */
  hasSummary: boolean
  /** How long since the recording row last changed, measured server-side. */
  recordingIdleMs: number | null
  /**
   * A transcript correction's re-run of the summary and items is in flight
   * (#14298) — bounded by the settle window where it is read. A finished
   * note reads SUMMARIZING again until it lands.
   */
  summaryRerunPending?: boolean
  /**
   * A forced re-run ("Re-run summary and items", #15187) has rewritten the
   * summary and is re-finding the items (`noteAiRerunStep`,
   * `./note-ai-rerun-plan.ts`) — bounded by the settle window where it is
   * read. A finished note reads EXTRACTING until it lands; while the summary
   * is still being rewritten the read passes `summaryRerunPending` instead.
   */
  extractionRerunPending?: boolean
  /**
   * A manual "Try the summary again" (`retryNoteProcessing`) holds its lease
   * and is still running its passes after the response (#15854,
   * `readNotePassRetryPending`). The row reads the step being redone —
   * SUMMARIZING while the summary is missing, EXTRACTING once it is there —
   * rather than PASS_FAILED until the retry settles. Absent reads as false.
   */
  passRetryPending?: boolean
  /**
   * The note's SHARED summary is outdated (#15145,
   * {@link summaryOutdatedByTranscript}): a transcription job under the note
   * completed after the pass that wrote it started — a note recorded in parts
   * still holds an earlier part's summary while the newest part's is written.
   * Such a summary does not count as present: the row reads SUMMARIZING until
   * a newer one lands, then the missing-pass rules apply. Absent reads as
   * false.
   */
  summaryOutdated?: boolean
  /**
   * The note has NO shared summary, and a summary pass CONCLUDED over every
   * transcript it holds that there was nothing to write (#15993,
   * {@link summaryPassConcluded}) — a manual retry whose result was a final
   * skip stamped `Note.summaryCurrentAsOf`. Such a summary is not missing:
   * the same transcript answers the same way, so the row must not read
   * PASS_FAILED and offer a retry that can only skip again. Absent reads as
   * false.
   */
  summaryConcluded?: boolean
  /**
   * What the failed-pass state needs (#14362). Absent keeps the pre-#14362
   * settle-to-DONE, because without it a failure and an unconfigured
   * environment look the same.
   */
  passes?: {
    /** The note has at least one stored transcript segment. */
    hasTranscript: boolean
    /** A model credential is configured, so the passes could have run. */
    derivationReady: boolean
    /**
     * The automatic paths spent every attempt on the note's SUMMARY and none
     * is running (#14432, `passAttemptsExhausted`). Nothing will retry it,
     * so the row may say it failed without waiting out the settle window.
     */
    summaryExhausted?: boolean
    /** The same, for the latest recording's EXTRACTION. */
    extractionExhausted?: boolean
  }
}

const UPLOAD_STATUSES = new Set(['CAPTURING', 'UPLOADING'])
const PRE_TRANSCRIPT_STATUSES = new Set(['UPLOADED', 'TRANSCRIBING'])
const POST_TRANSCRIPT_STATUSES = new Set(['TRANSCRIBED', 'EXTRACTING', 'READY'])

/**
 * Whether the note's shared summary is OUTDATED by a transcript (#15145) —
 * the summary stage's own rule (`sharedSummaryOutdatedSql`,
 * `@/lib/meetings/summary-stage`), so the processing row, the retry plan and
 * the backstop that re-summarises agree on which summaries are stale.
 *
 * `summaryCurrentAsOf` is `Note.summaryCurrentAsOf`: when the pass that
 * wrote the summary STARTED (its claim, before it read the transcripts) —
 * not when it saved. A part that finished transcribing mid-pass is not in
 * that summary although the save came later. `newestTranscribedAt` is the
 * newest SUCCEEDED job's `completedAt` across EVERY recording of the note.
 *
 * Strictly after, as the SQL compares. Either fact unknown claims nothing: a
 * NULL stamp (a summary from before the column, or a writer that does not
 * stamp it) compares as not outdated in the SQL too, and a note with no
 * finished job has no transcript to be behind.
 */
export function summaryOutdatedByTranscript(input: {
  summaryCurrentAsOf: Date | null
  newestTranscribedAt: Date | null
}): boolean {
  if (input.summaryCurrentAsOf === null || input.newestTranscribedAt === null) {
    return false
  }
  return (
    input.newestTranscribedAt.getTime() > input.summaryCurrentAsOf.getTime()
  )
}

/**
 * Whether a summary pass CONCLUDED over every transcript the note holds
 * (#15993): `summaryCurrentAsOf` is stamped and no SUCCEEDED transcription
 * job completed after it. The callers apply it only where the note has no
 * shared summary — with one, {@link summaryOutdatedByTranscript} is the
 * question — so a true here means "a pass ran to a final answer over these
 * transcripts and had nothing to write", which re-running cannot change.
 *
 * Either fact unknown claims nothing, as in {@link summaryOutdatedByTranscript}:
 * a note with no stamp has had no such pass, and a note with no finished job
 * has no transcript for one to have covered.
 */
export function summaryPassConcluded(input: {
  summaryCurrentAsOf: Date | null
  newestTranscribedAt: Date | null
}): boolean {
  if (input.summaryCurrentAsOf === null || input.newestTranscribedAt === null) {
    return false
  }
  return !summaryOutdatedByTranscript(input)
}

/** Which stage the note's capture is in. See the module header. */
export function noteProcessingStage(
  input: NoteProcessingInput,
): NoteProcessingStage {
  const { recordingStatus, transcriptionStatus } = input
  if (recordingStatus === null) return 'NONE'

  if (recordingStatus === 'FAILED' || transcriptionStatus === 'FAILED') {
    return 'FAILED'
  }
  if (UPLOAD_STATUSES.has(recordingStatus)) return 'UPLOADING'

  // The job outranks the label: a retry runs over a recording already
  // labelled past transcription.
  if (transcriptionStatus === 'PENDING' || transcriptionStatus === 'RUNNING') {
    return 'TRANSCRIBING'
  }
  if (PRE_TRANSCRIPT_STATUSES.has(recordingStatus)) return 'TRANSCRIBING'

  if (!POST_TRANSCRIPT_STATUSES.has(recordingStatus)) {
    // A status this code does not know. Inventing a stage would be a guess;
    // claiming work is running would be a spinner that never stops.
    return 'DONE'
  }

  // After every transcription answer and before the settle: a correction
  // re-run is work genuinely happening now on a note that had finished.
  if (input.summaryRerunPending === true) return 'SUMMARIZING'
  if (input.extractionRerunPending === true) return 'EXTRACTING'

  // An import arrives transcribed and summarised; the only pass left is the
  // importer's extraction. Finding items until it lands or the row settles,
  // then DONE — never PASS_FAILED, whose retry would decline (no
  // transcription job) or, worse, run a model the synopsis gate refused.
  if (input.recordingMode === IMPORTED_RECORDING_MODE) {
    const importSettled =
      input.recordingIdleMs !== null &&
      input.recordingIdleMs > NOTE_PROCESSING_SETTLE_MS
    return recordingStatus === 'READY' || importSettled ? 'DONE' : 'EXTRACTING'
  }

  // #15145 — an outdated (earlier part's) summary is not this one's.
  // #15993 — a pass that concluded there was nothing to write is not a
  // missing summary: re-running it over the same transcript answers the same.
  const hasSummary =
    (input.hasSummary || input.summaryConcluded === true) &&
    input.summaryOutdated !== true

  const extractionFinished = recordingStatus === 'READY'
  if (hasSummary && extractionFinished) return 'DONE'

  // A manual retry is redoing a missing pass right now (#15854): work is
  // genuinely happening, whatever the settle clock or the attempt budget say.
  if (input.passRetryPending === true) {
    return hasSummary ? 'EXTRACTING' : 'SUMMARIZING'
  }

  // AN EXHAUSTED PASS IS FAILED NOW (#14432). The backstop sweep retries a
  // missing pass up to `NOTETAKER_PASS_MAX_ATTEMPTS` times; once those are
  // spent nothing else will, so waiting out the settle window would only
  // be ten more minutes of a spinner for work nobody is doing.
  const { passes } = input
  if (passes?.summaryExhausted === true || passes?.extractionExhausted) {
    const missing = noteMissingPasses({
      recordingStatus,
      hasSummary,
      hasTranscript: passes.hasTranscript,
    })
    const exhausted =
      (passes.summaryExhausted === true && missing.includes('summary')) ||
      (passes.extractionExhausted === true && missing.includes('extraction'))
    if (exhausted) return 'PASS_FAILED'
  }

  const settled =
    input.recordingIdleMs !== null &&
    input.recordingIdleMs > NOTE_PROCESSING_SETTLE_MS
  if (settled) {
    const missing =
      passes?.derivationReady === true
        ? noteMissingPasses({
            recordingStatus,
            hasSummary,
            hasTranscript: passes.hasTranscript,
          })
        : []
    return missing.length > 0 ? 'PASS_FAILED' : 'DONE'
  }

  return hasSummary ? 'EXTRACTING' : 'SUMMARIZING'
}

/**
 * What the note page's processing-status read answers (#14360) — the stage
 * fields and nothing else. No title, body, summary or transcript text: it is
 * polled every few seconds, and a poll that carried content would be a second
 * content read with a looser shape than the page's.
 *
 * Plain data (the timestamp is an ISO string), so it crosses the wire as-is.
 */
export type NoteProcessingStatus = {
  stage: NoteProcessingStage
  /** The latest job, for the progress line and the failure reason. */
  transcription: MeetingTranscriptionStatus | null
  /**
   * When the latest recording row last changed — the closest stored fact to
   * "when the stage last moved". Null for a notes-only note.
   */
  stageChangedAt: string | null
  /** Whether the note has transcript segments to read. */
  hasTranscript: boolean
  /**
   * The stage is SUMMARIZING because the note is being re-derived with audio
   * that arrived after Stop (#14423) — `@/lib/notetaker/late-audio-pending`.
   * Absent reads as false.
   */
  lateAudioPending?: boolean
}

/** Whether the row is showing work in flight (and so should refresh). */
export function isNoteProcessingActive(stage: NoteProcessingStage): boolean {
  return (NOTE_PROCESSING_STEPS as readonly string[]).includes(stage)
}

export type NoteProcessingStepState = {
  step: NoteProcessingStep
  state: 'done' | 'active' | 'pending'
}

/**
 * Each step's state for a stage in flight or DONE. FAILED, PASS_FAILED and
 * NONE render no steps — the row says what happened instead — so for those
 * every step reads `pending` and the caller should not draw them.
 */
export function noteProcessingStepStates(
  stage: NoteProcessingStage,
): NoteProcessingStepState[] {
  const current =
    stage === 'DONE'
      ? NOTE_PROCESSING_STEPS.length
      : (NOTE_PROCESSING_STEPS as readonly string[]).indexOf(stage)
  return NOTE_PROCESSING_STEPS.map((step, index) => ({
    step,
    state: index < current ? 'done' : index === current ? 'active' : 'pending',
  }))
}

/**
 * Whether the page may say "Nothing came out of this meeting".
 *
 * Only once EXTRACTION FINISHED (`READY`) and found nothing. A `PASS_FAILED`
 * note at `READY` qualifies: what failed there is the summary, and the search
 * it would report on did finish. A pipeline that
 * settled at `TRANSCRIBED` never searched, and a running job has not finished
 * searching — in both, the sentence would be a claim about a search that has
 * not happened. A notes-only note has no search to report on at all.
 */
export function showNothingCameOut(input: {
  stage: NoteProcessingStage
  recordingStatus: string | null
  itemCount: number
}): boolean {
  return (
    (input.stage === 'DONE' || input.stage === 'PASS_FAILED') &&
    input.recordingStatus === 'READY' &&
    input.itemCount === 0
  )
}

/**
 * When Stop was pressed on a recording (ISO), or `null` when that is not
 * known (#14365, for `note.revealed`'s `msFromStop`).
 *
 * No column stores the stop: the recording row is created when capture
 * STARTS (`capture-note.ts`) and `durationMs` is the captured length, so the
 * stop is the one plus the other. While the length is unknown, so is the
 * stop — it is never guessed from `updatedAt`, which every later stage moves.
 */
export function recordingStoppedAt(
  recording: { createdAt: Date; durationMs: number | null } | null,
): string | null {
  if (recording === null || recording.durationMs === null) return null
  return new Date(
    recording.createdAt.getTime() + recording.durationMs,
  ).toISOString()
}
